import { Button, Flexbox, Icon, openInIFrame, requireFocusContext } from 'st-app';
import { DASHBOARD_VIEW_GU, requireRoleDashboardContext } from '../RoleDashboardContext';
import { createEffect, createProxySignal, createSignal, onMount, onCleanup, uuidv4 } from 'common';
import styles from '../sis-role-dashboard.module.css';

// Module-level signal to store captured SERVER_STATE from Server_State CLIENT_ACTION responses.
// This is accessed by both the ViewDrawer component and the openViewInDrawer function.
const [capturedServerState, setCapturedServerState] = createSignal<any>(null);

export function ViewDrawer() {
    const ctx = requireRoleDashboardContext();
    const focus = requireFocusContext();
    const [collapsed, setCollapsed] = createProxySignal(
        () => !ctx.viewDrawerRequest(),
        (value: boolean) => {
            if (value) {
                ctx.setViewDrawerRequest('');
            }
        }
    );

    // Initialize ST.Component namespace and bridge functions IMMEDIATELY
    // This must happen before any iframe content loads
    const ST = (window as any).ST || ((window as any).ST = {});
    ST.Component = ST.Component || {};

    // Internal state — owned entirely by this component, never read or written
    // by legacy callsites. Tracks whether the drawer's iframe content is mid-swap
    // so requestClose() can ignore stale close events that fire from the previous
    // view as it gets replaced.
    const [pendingSwap, setPendingSwap] = createSignal(false);

    // Bridge surface targeted by the legacy framework. Three semantic methods,
    // no shared mutable flags. All swap/close decisions are made internally
    // by this component; legacy callsites just signal intent.
    ST.Component.ViewDrawer = {

        // Legacy is asking the host to handle an Add request. Returns true if
        // handled (caller skips its legacy overlay path), false to fall through.
        routeAddRequest: (reqXmlOrJson: string | object, title?: string): boolean => {
            setPendingSwap(true);
            ctx.setViewDrawerTitle(title ?? '');
            ctx.setViewDrawerRequest(reqXmlOrJson as any);
            return true;
        },

        // Legacy is signaling that the current view wants to close. The bridge
        // decides whether to actually close the drawer based on its internal
        // state — if a swap is in progress, the close is from the view being
        // replaced and we ignore it.
        requestClose: (_reason: 'viewSaved' | 'viewCancelled' | 'windowRemoved' | 'clientAction'): boolean => {
            if (pendingSwap()) {
                return true;
            }
            setCollapsed(true);
            ctx.triggerRefresh();
            return true;
        },

        // Forward a CLIENT_ACTION up because the originating window is closing
        // or the action is parent-bound. Handles types the bridge knows about;
        // returns false for unknown types so the legacy traditional path can
        // continue (chooser callbacks, refresh, etc.).
        parseClientAction: (xmlString: string): boolean => {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
            const node = xmlDoc.documentElement;
            const clientActionType = node.getAttribute('TYPE');

            if (clientActionType === 'Server_State') {
                const serverStateEl = node.querySelector('SERVER_STATE');
                if (serverStateEl) {
                    const dElements = serverStateEl.querySelectorAll('D');
                    const serverStateData: any[] = [];

                    dElements.forEach(d => {
                        const k = d.getAttribute('K');
                        const v = d.getAttribute('V');
                        if (k) {
                            serverStateData.push({
                                '@K': k,
                                '@V': v || ''
                            });
                        }
                    });

                    if (serverStateData.length > 0) {
                        setCapturedServerState({ 'D': serverStateData });
                    }
                }
                return true;
            }
            else if (clientActionType === 'Close_Window') {
                return ST.Component.ViewDrawer.requestClose('clientAction');
            }
            else if (clientActionType === 'Open_Add_Window') {
                const identity = node.getAttribute('IDENTITY');

                if (!identity || identity.trim() === '') {
                    return false;
                }

                const focusKey = focus.focusKey();
                if (!focusKey) {
                    return false;
                }

                const requestJson = {
                    'REV_REQUEST': {
                        'EVENT': {
                            '@NAME': 'WDWActionEvent',
                            'REQUEST': {
                                '@WINDOW_ID': 'Overlay4',
                                'REV_RENDER': {
                                    '@ADD_TYPE': 'EXT_CONFIRM',
                                    '@ADD_ID': identity,
                                    '@FOCUS_KEY': focusKey,
                                    'VIEW': {
                                        '@MODE': 'ADD',
                                        '@GUID': '3D8BCF52-C4A1-485B-A2E6-C385FE1B6A9E',
                                        '@RENDER_VIEW': 'YES'
                                    }
                                },
                                'PASS_THROUGH': {
                                    '@ParentViewGU': '3D8BCF52-C4A1-485B-A2E6-C385FE1B6A9E',
                                    '@ParentViewClientID': 'PrimaryView',
                                    '@ViewType': 'REV_EXTERNAL_OBJECT_SELECTION'
                                },
                                'SERVER_STATE': capturedServerState()
                            }
                        }
                    }
                };

                return ST.Component.ViewDrawer.routeAddRequest(requestJson, 'Student Add');
            }

            // Unhandled action type — let the traditional RT path process it
            return false;
        }
    };

    let iframe: HTMLIFrameElement | undefined;
    onMount(() => {
        createEffect(() => {
            if (ctx.viewDrawerRequest() && iframe) {
                openInIFrame(ctx.viewDrawerRequest(), iframe);
            }
        });

        createEffect(() => {
            document.body.classList.toggle('drawer-open', !collapsed());
        });

        // Cleanup on unmount
        onCleanup(() => {
            document.body.classList.remove('drawer-open');
            if (ST.Component?.ViewDrawer) {
                delete ST.Component.ViewDrawer;
            }
        });
    });

    const iframeId = 'iframe_' + uuidv4().replaceAll('-', '');
    return <Flexbox dir="vertical" class={styles.drawer} data-anim={collapsed() ? 'out' : 'in'} >
        <Flexbox dir="horizontal" class={styles['drawer-header']} middle onClick={() => setCollapsed(true)} >
            <Flexbox.Item as={Button} type="link" onClick={() => setCollapsed(true)} >
                <Icon src="far fa-chevron-down" />
            </Flexbox.Item>
            <Flexbox.Item as="h1" auto>{ctx.viewDrawerTitle()}</Flexbox.Item>
            <Flexbox.Item>
                <Icon src="far fa-chevron-down" />
            </Flexbox.Item>
        </Flexbox>
        <Flexbox.Item auto>
            <iframe ref={iframe} id={iframeId} name={iframeId} src="about:blank" onLoad={() => setPendingSwap(false)} />
        </Flexbox.Item>
    </Flexbox>;
}

export interface StudentContext {
    studentGU: string;
    ssyGU: string;
    stuName: string;
}

export function openViewInDrawer(viewName: string, viewGU: string, viewType: string, detached: boolean,
    tabGU?: string, studentCtx?: StudentContext) {
    const ctx = requireRoleDashboardContext();
    const focus = requireFocusContext();

    if (detached) {
        const st = (window.top ?? window)?.ST;
        if (st) {
            st.LoadView(viewType, viewGU, detached, false);
        }
    }
    else {
        const focusKey = focus.focusKey();
        if (!focusKey) {
            console.error('[openViewInDrawer] Focus key not available');
            return;
        }

        const dashboardGuid = DASHBOARD_VIEW_GU;

        const request: Record<string, any> = {
            '@WINDOW_ID': 'Overlay3',
            'REV_RENDER': {
                '@VIEW': viewGU,
                '@PREPEND_TITLE': `${viewName}:   `,
                '@FOCUS_KEY': focusKey,
                'VIEW': {
                    '@MODE': 'ADD',
                    '@GUID': viewGU,
                    '@RENDER_VIEW': 'YES'
                },
                'CLIENT_STATE': {}
            },
            'PASS_THROUGH': {
                '@ParentViewGU': dashboardGuid,
                '@ParentViewClientID': 'PrimaryView'
            }
        };

        if (tabGU) {
            request['REV_RENDER']['@TAB'] = tabGU;
        }

        if (studentCtx) {
            request['REV_RENDER']['@ADD_ID'] = studentCtx.studentGU;
            request['REV_RENDER']['@SUBTITLE'] = studentCtx.stuName;

            request['REV_DATA_GROUP'] = {
                'REV_ELEMENT': [
                    { '@SRC_OBJECT': '0AFBF98B-3A86-4173-9BCC-E43C032ABEC4', '@SRC_ELEMENT': 'StudentSchoolYearGU', '#cdata': studentCtx.ssyGU },
                    { '@SRC_OBJECT': '4AEDE264-269B-497C-9B8E-0CA1AC6F94BF', '@SRC_ELEMENT': 'StudentGU', '#cdata': studentCtx.studentGU }
                ]
            };

            request['PASS_THROUGH']['GROUP_FIELDS'] = {
                'REV_ELEMENT': [
                    { '@SRC_OBJECT': '4AEDE264-269B-497C-9B8E-0CA1AC6F94BF', '@SRC_ELEMENT': 'StudentGU', '#cdata': studentCtx.studentGU }
                ]
            };
        }

        const requestJson = {
            'REV_REQUEST': {
                'EVENT': {
                    '@NAME': 'Rev_Get_AddView',
                    'REQUEST': request
                }
            }
        };

        ctx.setViewDrawerTitle(viewName);
        ctx.setViewDrawerRequest(requestJson);
    }
}
